import { STATUS_REPORT_EVENT_MEMBERS, STATUS_REPORT_HEADER_MEMBERS, STATUS_REPORT_REQUIRED_SECTIONS, STATUS_REPORT_SECTIONS, StatusReportSectionEtagsSchema } from "./status-report.schemas.js";
import { statusReportAppEtag, statusReportEtag, statusReportSectionEtag, statusReportSectionEtags } from "./status-report-hash.js";
import { z } from "zod";
//#region src/status-report-delta.ts
/**
* The delta protocol of the status report (2026-10-08-status-report-budget WS-1, D-6 to D-11,
* D-13): the capability name, the byte budget, the size metric name, the two answer shapes of the
* CP, and the three pure functions that build, apply and judge a delta.
*
* Who calls it: the zion-operator (WS-4) builds deltas, measures bodies and reads the answers;
* release-engine (WS-3) judges and applies deltas and writes the answers; release-engine's fake
* operator in its tests builds deltas with the same function the operator uses.
*
* Pure: no I/O, no clock, no state.
*/
/**
* The capability an operator lists in `StatusReport.capabilities` when it can send
* `sectionEtags`, deltas and gzip bodies, and read the 409 and the `requestFullStatusReport` flag
* (D-13). The literal is `'status-delta'`, inside `CAPABILITY_PATTERN`.
*
* The reader is release-engine's desired-state projection. It sends the envelope member
* `statusReportAccepts` only to a data plane whose last report lists this name, the gate pattern of
* `REGIONAL_POOLS_CAPABILITY`. So an operator that predates deltas never sees the member, and its
* envelope etag does not move. Both ends import this constant; neither spells the string.
*/
const STATUS_DELTA_CAPABILITY = "status-delta";
/**
* The byte budget of one full status report: 393,216 bytes, 384 KiB (D-10). The operator measures
* {@link statusReportBodyBytes} of a full report before any gzip. Over the budget, it trims the
* members of `STATUS_REPORT_TRIMMABLE_MEMBERS` in that order and names them in `truncated`. Still
* over, it sends the report whole and the near-budget alert fires.
*
* The figure sits 128 KiB under the route limit {@link STATUS_REPORT_ROUTE_LIMIT_BYTES}
* (524,288), so a report at the budget still fits the route when the gateway or a CP build reads it
* uncompressed.
*
* A delta has the same budget (finding WS-1-R3). The operator never gzips a delta, and a delta
* after a mass change carries every app body plus the whole etag map, so it can be larger than the
* full report. {@link buildStatusReportDelta} refuses a delta over the budget with a `RangeError`,
* and the operator then sends the full report, trimmed to the budget and gzip when the CP accepts
* gzip (R-036). So a delta that goes out is at most 393,216 bytes and never meets the route limit.
*/
const STATUS_REPORT_BUDGET_BYTES = 393216;
/**
* The largest status report body that release-engine's route takes, in bytes: 512 KiB. The route
* answers 413 for an identity body one byte over it, and for the compressed bytes of a gzip body
* over it; its gzip interceptor inflates under the same cap. release-engine keeps it under its
* setting's name `STATUS_REPORT_BODY_LIMIT_BYTES`, and the operator reads it to decide whether a
* full report over {@link STATUS_REPORT_BUDGET_BYTES} can still carry its etag map (R-043).
*/
const STATUS_REPORT_ROUTE_LIMIT_BYTES = 524288;
/**
* The family name of the operator's report-size histogram (WS-4 writes it, WS-5 keeps it and alerts
* on it): `zion_operator_status_report_bytes`. One observation per report the operator sends, of
* {@link statusReportBodyBytes} before any gzip, with the label `kind` from
* {@link STATUS_REPORT_KINDS}. The CP gateway keep lists and `emitted-metrics.json` name it byte
* for byte.
*/
const STATUS_REPORT_SIZE_METRIC = "zion_operator_status_report_bytes";
/** The two kinds of status report body, and the values of the `kind` label of {@link STATUS_REPORT_SIZE_METRIC}. */
const STATUS_REPORT_KINDS = ["full", "delta"];
/**
* Whether a status report body is a delta: `true` exactly when it carries `baseEtag`. Because the
* full arm of `StatusReportBodySchema` refuses that key and the delta arm requires it, this test
* tells the two arms of a parsed body apart.
*
* Generic over the body type, so it narrows both {@link StatusReportBody} and the type that
* `z.infer` gives for `StatusReportBodySchema`, which release-engine's DTO uses. Pure.
*/
function isStatusReportDelta(body) {
	return Object.hasOwn(body, "baseEtag");
}
/** The kind of a status report body: `'delta'` when {@link isStatusReportDelta}, `'full'` otherwise. Pure. */
function statusReportKindOf(body) {
	return isStatusReportDelta(body) ? "delta" : "full";
}
/**
* The size of a status report body as the budget and the size metric count it: the UTF-8 bytes of
* `JSON.stringify(body)`, before any gzip (`Buffer.byteLength`). A character outside ASCII counts as
* its UTF-8 length, so `é` counts 2 and an emoji counts 4.
*
* Who calls it: the operator, for the budget check and the metric. Errors: the `TypeError` of
* `JSON.stringify` for a cycle or a bigint. Pure.
*/
function statusReportBodyBytes(body) {
	return Buffer.byteLength(JSON.stringify(body), "utf8");
}
/**
* The 2xx answer body of `POST /v1/operator/data-planes/:dpId/status` (D-7). `acknowledged` is
* `true`, as today. `requestFullStatusReport: true` asks the operator to send its next report in
* full. The CP sets it on the answer to a full report whose `sectionEtags` claim differs from the
* map the CP computes from the body ({@link judgeStatusReportFull}, finding WS-1-R1). The two
* builds of the hash helper then differ, and a delta against the claim could only answer 409. A
* delta that the CP cannot apply (no stored map, or another base) gets the 409 answer, never this
* flag.
*
* Absent means "send a delta if you can". On an answer with the flag the operator sends its next
* report in full, and it never sends a delta against the claim of the report it answers.
*
* Strict. Today's body `{ acknowledged: true }` parses to itself. `false` in either member, or any
* other key, is a `ZodError`; the operator treats a body that does not parse as an accepted report
* with no request. Pure.
*/
const StatusReportAckSchema = z.object({
	acknowledged: z.literal(true),
	requestFullStatusReport: z.literal(true).optional()
}).strict();
/**
* The coded `error` of the 409 answer (D-7): `status_report_base_mismatch`. It matches the coded
* error grammar of `AllExceptionsFilter` in `@zion/sdk-nestjs` (a lowercase word with digits and
* underscores), so the filter keeps the payload keys in the body.
*/
const STATUS_REPORT_BASE_MISMATCH_ERROR = "status_report_base_mismatch";
/** The fixed `message` of the 409 answer. It names no data plane and no etag. */
const STATUS_REPORT_RESYNC_MESSAGE = "the status report base does not match the stored report; send a full report";
/**
* The payload release-engine gives to `new ConflictException(...)` for a delta whose `baseEtag`
* differs from the stored one (D-7). `AllExceptionsFilter` merges it with `statusCode`, `message`,
* `timestamp` and `path`, and the result parses with {@link StatusReportResyncBodySchema}. Frozen,
* so no caller can change the shared object.
*/
const STATUS_REPORT_RESYNC_PAYLOAD = Object.freeze({
	error: STATUS_REPORT_BASE_MISMATCH_ERROR,
	message: STATUS_REPORT_RESYNC_MESSAGE,
	requestFullStatusReport: true
});
/**
* The 409 answer body of the status report route (D-7): the CP holds a different base than the
* delta names, and the operator must send its next report in full.
*
* - `statusCode`: `409`.
* - `error`: {@link STATUS_REPORT_BASE_MISMATCH_ERROR}.
* - `requestFullStatusReport`: `true`.
* - `message`: a string; release-engine sends {@link STATUS_REPORT_RESYNC_MESSAGE}.
* - `timestamp` and `path`: optional strings, which `AllExceptionsFilter` adds.
*
* Strict. A conforming object parses to itself; anything else is a `ZodError`. Pure.
*/
const StatusReportResyncBodySchema = z.object({
	statusCode: z.literal(409),
	error: z.literal(STATUS_REPORT_BASE_MISMATCH_ERROR),
	requestFullStatusReport: z.literal(true),
	message: z.string(),
	timestamp: z.string().optional(),
	path: z.string().optional()
}).strict();
/** The keys of `source` named in `keys` whose value is defined, so an absent member stays absent. */
function pickDefined(source, keys) {
	const out = {};
	for (const key of keys) if (source[key] !== void 0) out[key] = source[key];
	return out;
}
/** The header and event members of a body, copied when present. */
function carriedMembers(body) {
	return pickDefined(body, [...STATUS_REPORT_HEADER_MEMBERS, ...STATUS_REPORT_EVENT_MEMBERS]);
}
/** The sections that a full report may omit: every section less the three it always carries. */
const OPTIONAL_SECTIONS = STATUS_REPORT_SECTIONS.filter((section) => !STATUS_REPORT_REQUIRED_SECTIONS.includes(section));
/**
* The value of `record` under `key` when `key` is an own key of `record`, else `undefined`. An app
* key is any string, and a plain index reads an inherited member for an absent key: `constructor`
* gives the `Object` function and `toString` a function. Every read of an app map by key goes
* through this helper.
*/
function ownEntry(record, key) {
	return record !== void 0 && Object.hasOwn(record, key) ? record[key] : void 0;
}
/** Copy one member from `source` to `target`. Generic, so the key and the value stay correlated. */
function copyMember(target, source, key) {
	target[key] = source[key];
}
/** The value a delta keeps for one section: its body when present, else the base's value. */
function keptSection(base, delta, section) {
	const body = delta[section];
	const value = body !== void 0 ? body : base[section];
	if (value === void 0) throw new RangeError(`applyStatusReportDelta: the delta keeps section ${section}, which the base does not hold`);
	return value;
}
/**
* The delta that stands for a full report against an accepted etag map (D-6, D-8).
*
* Who calls it: the operator, for each report when the envelope says the CP accepts deltas and it
* holds the map of its last accepted report; release-engine's fake operator in tests.
*
* Output:
* - every header and event member that the report carries, unchanged;
* - `baseEtag`: `statusReportEtag(accepted)`;
* - `sectionEtags`: `statusReportSectionEtags(report)`; the operator keeps it as the pending
*   base and promotes it to accepted only on a 2xx answer;
* - the body of each section whose etag differs from `accepted` or is new;
* - `apps` with each app whose etag differs from `accepted` or is new, and only when one does.
*
* A report equal to the base, apart from header and event members, gives a delta with no body
* (D-8); the operator still posts it.
*
* The section and app etags are those of the report as `StatusReportSchema` parses it
* (`statusReportSectionEtags`), and the bodies are the report's own values. An app travels only
* under a key that the parsed `apps` record keeps, so an own `__proto__` app key, which the CP's
* parse drops, never travels.
*
* Errors, each a `RangeError` after which the caller sends the report in full:
* - the report carries `truncated`, because only a full report is trimmed (D-10);
* - {@link statusReportBodyBytes} of the delta is over {@link STATUS_REPORT_BUDGET_BYTES}
*   (finding WS-1-R3). A delta of exactly the budget goes out.
*
* It also throws the errors of `statusReportSectionEtags` for a report that `StatusReportSchema`
* refuses. Pure; neither input is changed.
*/
function buildStatusReportDelta(report, accepted) {
	if (report.truncated !== void 0) throw new RangeError("buildStatusReportDelta: a trimmed report goes out as a full report");
	const etags = statusReportSectionEtags(report);
	const bodies = {};
	for (const section of STATUS_REPORT_SECTIONS) {
		const etag = etags[section];
		if (etag !== void 0 && etag !== accepted[section]) copyMember(bodies, report, section);
	}
	const changedApps = Object.entries(report.apps).filter(([appKey]) => Object.hasOwn(etags.apps, appKey) && ownEntry(etags.apps, appKey) !== ownEntry(accepted.apps, appKey));
	if (changedApps.length > 0) bodies.apps = Object.fromEntries(changedApps);
	const delta = {
		...carriedMembers(report),
		...bodies,
		baseEtag: statusReportEtag(accepted),
		sectionEtags: etags
	};
	if (statusReportBodyBytes(delta) > 393216) throw new RangeError("buildStatusReportDelta: a delta over the budget goes out as a full report");
	return delta;
}
/**
* The full report a delta stands for, given the full report the CP built from its base (D-6). This
* is the reference meaning of a delta: release-engine applies it column by column with the same
* result, and a test proves `statusReportSectionEtags(apply(base, delta))` equals
* `delta.sectionEtags` for every delta that {@link buildStatusReportDelta} makes.
*
* Output:
* - the header and event members of the delta; one the delta omits is absent;
* - for each section with a key in `delta.sectionEtags`: the delta's body when present, else the
*   base's value; a section with no key is absent;
* - `apps`: each key of `delta.sectionEtags.apps`, from the delta's body when present, else from
*   the base; an app with no key is removed. Only an own key of `delta.apps` or `base.apps` gives a
*   body, so an app key such as `constructor` or `__proto__` is an app like any other;
* - `sectionEtags`: the delta's map; `truncated` is absent.
*
* Errors: a `RangeError` naming the member when the delta keeps a section or an app (a key with no
* body) that the base does not hold. {@link judgeStatusReportDelta} refuses such a delta first, so
* the CP never reaches this error. Pure; neither input is changed.
*/
function applyStatusReportDelta(base, delta) {
	const sections = {};
	for (const section of OPTIONAL_SECTIONS) if (delta.sectionEtags[section] !== void 0) copyMember(sections, { [section]: keptSection(base, delta, section) }, section);
	const apps = Object.fromEntries(Object.keys(delta.sectionEtags.apps).map((appKey) => {
		const app = ownEntry(delta.apps, appKey) ?? ownEntry(base.apps, appKey);
		if (app === void 0) throw new RangeError(`applyStatusReportDelta: the delta keeps app ${appKey}, which the base does not hold`);
		return [appKey, app];
	}));
	return {
		...carriedMembers(delta),
		...sections,
		services: keptSection(base, delta, "services"),
		lastMigration: keptSection(base, delta, "lastMigration"),
		observability: keptSection(base, delta, "observability"),
		apps,
		sectionEtags: delta.sectionEtags
	};
}
/**
* The CP's verdict on a parsed delta against the etag map it stores for the data plane (D-7,
* "Refuse over accept" of the defaults table).
*
* Inputs: a delta that `StatusReportDeltaSchema` admitted, and the stored map as the CP reads it
* from `release.dp_live_status.report_hashes`, of any shape: `null` or `undefined` when the CP
* stores none. The judge parses the stored value with `StatusReportSectionEtagsSchema` itself
* (finding WS-1-R2). A map that a newer CP build wrote, with a section key this build does not
* know, then gives `resync` after a CP roll-back, and never a thrown `ZodError` that the
* exception filter turns into a 500.
*
* Output, in this order:
* 1. `resync` when no map is stored, when the stored value does not parse as an etag map, or when
*    `delta.baseEtag` differs from `statusReportEtag` of the parsed map. The base check comes
*    first, so a skew between two builds of the hash helper answers 409 and never 400.
* 2. `refuse` with every fault, sections in `STATUS_REPORT_SECTIONS` order and then apps in key
*    order, when any section or app has a changed or new etag and no body (`missing-body`), or
*    carries a body whose etag differs from its claim (`body-mismatch`).
* 3. `accept` otherwise.
*
* The judge reads an app body or a stored app etag only under an own key, so an app key that names
* an Object.prototype member (`constructor`, `toString`) gets a verdict like any other key.
*
* The data plane id never enters the verdict: the CP scopes every write by `:dpId` from its guard.
* Errors: none for an admitted delta, whatever the stored value. Pure.
*/
function judgeStatusReportDelta(delta, stored) {
	const parsed = StatusReportSectionEtagsSchema.safeParse(stored);
	if (!parsed.success || delta.baseEtag !== statusReportEtag(parsed.data)) return { kind: "resync" };
	return judgeAgainstBase(delta, parsed.data);
}
/** Steps 2 and 3 of {@link judgeStatusReportDelta}: the faults of a delta whose base matches `stored`. */
function judgeAgainstBase(delta, stored) {
	const faults = [];
	for (const section of STATUS_REPORT_SECTIONS) {
		const claimed = delta.sectionEtags[section];
		if (claimed === void 0) continue;
		const body = delta[section];
		if (body === void 0) {
			if (claimed !== stored[section]) faults.push({
				kind: "missing-body",
				section
			});
		} else if (statusReportSectionEtag(section, body) !== claimed) faults.push({
			kind: "body-mismatch",
			section
		});
	}
	for (const [appKey, claimed] of Object.entries(delta.sectionEtags.apps)) {
		const body = ownEntry(delta.apps, appKey);
		if (body === void 0) {
			if (claimed !== ownEntry(stored.apps, appKey)) faults.push({
				kind: "missing-body",
				section: "apps",
				app: appKey
			});
		} else if (statusReportAppEtag(body) !== claimed) faults.push({
			kind: "body-mismatch",
			section: "apps",
			app: appKey
		});
	}
	return faults.length === 0 ? { kind: "accept" } : {
		kind: "refuse",
		faults
	};
}
/**
* The CP's verdict on a full report that `StatusReportSchema` admitted (D-7, D-15).
*
* Who calls it: release-engine, for each full report body, before it writes the report and its
* map in one transaction; release-engine's fake operator in tests reads the same answer.
*
* Why the flag (finding WS-1-R1): a claim that differs from the CP's own map means the operator
* and the CP run two builds of the hash helper. The CP rolls out before the data plane, so this is
* the normal state during a release. Without the flag:
* 1. the operator promotes its claim to accepted on the 2xx answer;
* 2. its next delta names the etag of the claim as `baseEtag`;
* 3. {@link judgeStatusReportDelta} compares it with the CP's stored map and answers `resync`;
* 4. the CP answers 409 and applies nothing from that cycle;
* 5. the operator sends a full report, and step 1 comes again.
*
* So one report in two was lost for as long as the builds differed. With the flag, the operator
* sends each report in full until the builds agree: a skew costs bytes, and the CP applies every
* report.
*
* Output: a {@link StatusReportFullVerdict}. The `ack` parses with {@link StatusReportAckSchema}.
*
* Errors: the errors of `statusReportSectionEtags` for a report that `StatusReportSchema` refuses;
* none for an admitted report. Pure; the report is not changed.
*/
function judgeStatusReportFull(report) {
	const sectionEtags = statusReportSectionEtags(report);
	const claim = report.sectionEtags;
	return {
		sectionEtags,
		ack: claim !== void 0 && statusReportEtag(claim) !== statusReportEtag(sectionEtags) ? {
			acknowledged: true,
			requestFullStatusReport: true
		} : { acknowledged: true }
	};
}
//#endregion
export { STATUS_DELTA_CAPABILITY, STATUS_REPORT_BASE_MISMATCH_ERROR, STATUS_REPORT_BUDGET_BYTES, STATUS_REPORT_KINDS, STATUS_REPORT_RESYNC_MESSAGE, STATUS_REPORT_RESYNC_PAYLOAD, STATUS_REPORT_ROUTE_LIMIT_BYTES, STATUS_REPORT_SIZE_METRIC, StatusReportAckSchema, StatusReportResyncBodySchema, applyStatusReportDelta, buildStatusReportDelta, isStatusReportDelta, judgeStatusReportDelta, judgeStatusReportFull, statusReportBodyBytes, statusReportKindOf };

//# sourceMappingURL=status-report-delta.js.map